import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import useProjectRole from "@/hooks/useProjectRole";
import ProjectRoleSetup from "@/components/BasProcess/ProjectRoleSetup";
import CounterpartSteps from "@/components/BasProcess/CounterpartSteps";
import RoleStartGuide from "@/components/BasProcess/RoleStartGuide";
import {
  ROLE_VIEWS,
  getRoleContributions,
  getRoleSteps,
  type ProjectRole,
} from "@/lib/basProcess/roleViews";
import { STEP_STATUS_LABELS, type StepStatus } from "@/lib/basProcess/constants";
import {
  CheckCircle2,
  Circle,
  Clock,
  MinusCircle,
  ChevronRight,
  Hammer,
  Sparkles,
} from "lucide-react";

interface Props {
  projectId: string;
  onOpenLink?: (link: string) => void;
}



const statusIcon = (s: StepStatus) => {
  if (s === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (s === "in_progress") return <Clock className="h-4 w-4 text-amber-600" />;
  if (s === "not_applicable") return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
  return <Circle className="h-4 w-4 text-muted-foreground" />;
};

export const RoleHome: React.FC<Props> = ({ projectId, onOpenLink }) => {
  const { role, rolesInSystem, isOwner, canEdit, refresh } = useProjectRole(projectId);

  const [statuses, setStatuses] = useState<Record<string, StepStatus>>({});
  const [external, setExternal] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const load = async () => {
      const [{ data: steps }, { data: ext }] = await Promise.all([
        supabase.from("process_steps").select("doc_number, status").eq("project_id", projectId),
        supabase
          .from("external_step_completions")
          .select("doc_number, completed")
          .eq("project_id", projectId),
      ]);
      const map: Record<string, StepStatus> = {};
      (steps || []).forEach((r: any) => (map[r.doc_number] = r.status));
      setStatuses(map);
      const extMap: Record<string, boolean> = {};
      (ext || []).forEach((r: any) => (extMap[r.doc_number] = !!r.completed));
      setExternal(extMap);
    };
    load();
  }, [projectId]);

  const def = ROLE_VIEWS[role];
  const owned = useMemo(() => getRoleSteps(role), [role]);
  const contributes = useMemo(() => getRoleContributions(role), [role]);

  const statusOf = (doc: string, externallyManaged?: boolean): StepStatus => {
    if (externallyManaged && external[doc]) return "done";
    return statuses[doc] || "not_started";
  };

  const relevant = owned.filter((s) => statusOf(s.docNumber, s.externallyManaged) !== "not_applicable");
  const doneCount = relevant.filter((s) => statusOf(s.docNumber, s.externallyManaged) === "done").length;
  const pct = relevant.length ? Math.round((doneCount / relevant.length) * 100) : 0;
  const nextStep = relevant.find((s) => statusOf(s.docNumber, s.externallyManaged) !== "done");

  return (
    <div className="space-y-4">
      {isOwner && (
        <ProjectRoleSetup projectId={projectId} canEdit={canEdit} onSaved={refresh} />
      )}
      <RoleStartGuide role={role} projectId={projectId} onOpenLink={onOpenLink} />
      <CounterpartSteps
        projectId={projectId}
        ownRole={role}
        rolesInSystem={rolesInSystem}
        canEdit={canEdit}
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{def.role}</CardTitle>
          <p className="text-sm text-muted-foreground">{def.summary}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {owned.length > 0 ? (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {doneCount} av {relevant.length} egna dokument klara
                </span>
                <span className="font-medium">{pct} %</span>
              </div>
              <Progress value={pct} />
              {nextStep && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm">
                  <Hammer className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Nästa steg:</span>
                  <span className="font-medium">
                    Dok {nextStep.docNumber} · {nextStep.title}
                  </span>
                  {nextStep.appLink && onOpenLink && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto gap-1"
                      onClick={() => onOpenLink(nextStep.appLink!)}
                    >
                      Öppna <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Rollen äger inga egna dokument i systemet ännu – den bidrar till andras underlag.
            </p>
          )}
        </CardContent>
      </Card>

      {owned.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mina dokument</CardTitle>
            {owned.length > relevant.length && (
              <p className="text-xs text-muted-foreground">
                {owned.length - relevant.length} dokument är markerade som ej aktuella i projektet och
                visas inte här.
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-1">
            {relevant.map((s) => {
              const st = statusOf(s.docNumber, s.externallyManaged);
              return (
                <div
                  key={s.docNumber}
                  className="flex flex-wrap items-center gap-2 rounded-md px-2 py-2 hover:bg-muted/50"
                >
                  {statusIcon(st)}
                  <span className="text-sm font-medium">Dok {s.docNumber}</span>
                  <span className="text-sm text-muted-foreground">{s.title}</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {s.externallyManaged && external[s.docNumber]
                      ? "Kvitterad externt"
                      : STEP_STATUS_LABELS[st]}
                  </Badge>
                  {s.appLink && onOpenLink && (
                    <Button size="sm" variant="ghost" onClick={() => onOpenLink(s.appLink!)}>
                      Öppna
                    </Button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {contributes.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Jag bidrar till</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {contributes.map((s) => (
              <div key={s.docNumber} className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm">
                <span className="font-medium">Dok {s.docNumber}</span>
                <span className="text-muted-foreground">{s.title}</span>
                <Badge variant="secondary" className="ml-auto text-xs">
                  Ägs av {s.owner}
                </Badge>
                {s.appLink && onOpenLink && (
                  <Button size="sm" variant="ghost" onClick={() => onOpenLink(s.appLink!)}>
                    Öppna
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {def.planned.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-muted-foreground" /> På väg in för {def.role}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {def.planned.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RoleHome;
